import React from 'react';
import { Globe, Bot, Clock, RefreshCw } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Skeleton } from '../ui/skeleton';

interface UniverseStatsProps {
  totalSymbols: number;
  activeBots: number;
  lastScanAt: string | null;
  scanLoopRunning: boolean;
  loading: boolean;
}

function formatRelativeTime(iso: string | null): string {
  if (!iso) return 'Never';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  colorClass: string;
  loading: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ icon, label, value, colorClass, loading }) => {
  return (
    <Card className="flex-1 min-w-[140px]">
      <CardContent className="p-3 flex items-center gap-3">
        <div className={`w-10 h-10 rounded-[10px] flex items-center justify-center shrink-0 ${colorClass}`}>
          {icon}
        </div>
        <div>
          <span className="text-[0.7rem] font-medium text-muted-foreground block">
            {label}
          </span>
          {loading ? (
            <Skeleton className="w-[60px] h-7" />
          ) : (
            <div className="flex items-center">{value}</div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const UniverseStats: React.FC<UniverseStatsProps> = ({
  totalSymbols,
  activeBots,
  lastScanAt,
  scanLoopRunning,
  loading,
}) => {
  return (
    <div className="flex gap-3 mb-4 flex-wrap">
      <StatCard
        icon={<Globe className="w-5 h-5" />}
        label="Total Securities"
        value={
          <span className="text-lg font-bold">
            {totalSymbols.toLocaleString()}
          </span>
        }
        colorClass="bg-blue-500/10 text-blue-500"
        loading={loading}
      />
      <StatCard
        icon={<Bot className="w-5 h-5" />}
        label="Active Bots"
        value={
          <span className="text-lg font-bold">
            {activeBots}
          </span>
        }
        colorClass="bg-emerald-500/10 text-emerald-500"
        loading={loading}
      />
      <StatCard
        icon={<Clock className="w-5 h-5" />}
        label="Last Scan"
        value={
          <span className="text-base font-semibold">
            {formatRelativeTime(lastScanAt)}
          </span>
        }
        colorClass="bg-sky-500/10 text-sky-500"
        loading={loading}
      />
      <StatCard
        icon={<RefreshCw className="w-5 h-5" />}
        label="Scan Loop"
        value={
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
            scanLoopRunning
              ? 'bg-emerald-500/15 text-emerald-500'
              : 'bg-rose-500/15 text-rose-500'
          }`}>
            {scanLoopRunning ? 'Running' : 'Stopped'}
          </span>
        }
        colorClass={scanLoopRunning ? 'bg-emerald-500/10 text-emerald-500' : 'bg-rose-500/10 text-rose-500'}
        loading={loading}
      />
    </div>
  );
};

export default UniverseStats;
